import "./AllocatorNotesModal.scss";

import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { KaboodleToaster } from "components/common";
import { Checkbox } from "components/inputs";
import KaboodleModal from "components/Modals/KaboodleModal";
import Table, { SelectColumnWithSelectAll } from "components/Table/Table";
import { FEATURE_FLAGS } from "constants/FeatureFlags";
import { useEffect, useState } from "react";
import { useGenerateAIDocumentAnalysisMutation } from "services/document-management-api";
import { useGetDocumentsQuery } from "services/documents-api";
import {
  useGenerateCostProjectionAllocatorNotesMutation,
  useGenerateMSAAllocatorNotesMutation,
} from "services/msa-api";
import { useSelector } from "utils/redux";

import type { Column } from "react-data-grid";
import type { DocumentType } from "components/Documents/DocumentTypes";
type GenerateAllocatorNotesModalProps = {
  isOpen: boolean;
  onClose: () => void;
  serviceId: string;
  serviceType: "MSA" | "LCP" | "MCP" | "AMENDED_REVIEW" | "DOCS";
};

type OperationContext = {
  isSorting: boolean;
  isSummary: boolean;
  documentCount: number;
};

// helper function to get loading messages based on operation context
const getLoadingMessages = (context: OperationContext): string[] => {
  const { isSorting, isSummary } = context;

  if (isSorting && isSummary) {
    return [
      "Parsing document data...",
      "Sorting and analyzing documents...",
      "Generating summary...",
    ];
  }

  if (isSorting) {
    return [
      "Parsing document data...",
      "Analyzing document structure...",
      "Sorting documents...",
    ];
  }

  // summary only
  return [
    "Parsing document data...",
    "Analyzing medical records...",
    "Generating summary...",
  ];
};

// helper function to get loading modal heading
const getLoadingHeading = (context: OperationContext): string => {
  const { isSorting, isSummary } = context;

  if (isSorting && isSummary) {
    return "Sorting & Generating Summary";
  }

  if (isSorting) {
    return "Sorting Documents";
  }

  return "Generating Summary";
};

// helper function to get success message
const getSuccessMessage = (
  context: OperationContext,
  serviceType: string,
): string => {
  const { isSorting, isSummary, documentCount } = context;
  const docText = documentCount === 1 ? "document" : "documents";

  if (serviceType === "DOCS") {
    return `AI document analysis started for ${documentCount} ${docText}`;
  }

  if (isSorting && isSummary) {
    return `Started sorting and generating summary for ${documentCount} ${docText}`;
  }

  if (isSorting) {
    return `Started sorting ${documentCount} ${docText}`;
  }

  return `Started generating summary for ${documentCount} ${docText}`;
};

// helper function to get error message
const getErrorMessage = (
  context: OperationContext,
  serviceType: string,
): string => {
  const { isSorting, isSummary } = context;

  if (serviceType === "DOCS") {
    return "Failed to start AI document analysis. Please try again.";
  }

  if (isSorting && isSummary) {
    return "Failed to sort documents and generate summary. Please try again.";
  }

  if (isSorting) {
    return "Failed to start document sorting. Please try again.";
  }

  return "Failed to generate summary. Please try again.";
};

const GenerateAllocatorNotesModal = ({
  isOpen,
  onClose,
  serviceId,
  serviceType,
}: GenerateAllocatorNotesModalProps) => {
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<Set<number>>(
    new Set(),
  );
  const [generateMSAAllocatorNotes] = useGenerateMSAAllocatorNotesMutation();
  const [generateCostProjectionAllocatorNotes] =
    useGenerateCostProjectionAllocatorNotesMutation();
  const [generateAIDocumentAnalysis] = useGenerateAIDocumentAnalysisMutation();
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [messageIndex, setMessageIndex] = useState(0);
  const [generateSortedDocument, setGenerateSortedDocument] = useState(true);
  const [sortDocuments, setSortDocuments] = useState(false);
  const [generateSummary, setGenerateSummary] = useState(false);

  const currentService = useSelector(
    (state) => state.futureMedical.currentService,
  );
  const constants = useSelector((state) => state.shared.constants);
  const isModalAIServiceEnabled =
    constants?.FEATURE_FLAGS?.[FEATURE_FLAGS.MODAL_AI_SERVICE_ENABLED];

  const { data, isLoading } = useGetDocumentsQuery(
    {
      fileId: String(currentService?.file?.id),
      filters: {
        exclude_ai_generated: "true",
      },
    },
    {
      skip: !isOpen || !currentService?.file?.id,
    },
  );

  const columns: Column<DocumentType>[] = [
    {
      ...SelectColumnWithSelectAll,
      minWidth: 35,
      width: 35,
    },
    {
      key: "original_filename",
      name: "Document Name",
    },
    {
      key: "created",
      name: "Created",
      renderCell: ({ row }) => new Date(row.created).toLocaleDateString(),
    },
  ];

  // set all documents as selected by default
  useEffect(() => {
    if (data?.results && data.results.length > 0) {
      setSelectedDocumentIds(new Set(data.results.map((doc) => doc.id)));
    }
  }, [data?.results]);

  // progress bar effect
  useEffect(() => {
    if (!isGenerating) {
      setProgress(0);
      setMessageIndex(0);
      return;
    }

    const messageThresholds = [0, 20, 80];
    const totalDuration = 5000;
    const updateInterval = 50;
    const incrementPerInterval = 99 / (totalDuration / updateInterval);

    const progressInterval = setInterval(() => {
      setProgress((prevProgress) => {
        const newProgress = Math.min(99, prevProgress + incrementPerInterval);

        for (let i = 0; i < messageThresholds.length; i++) {
          if (
            prevProgress < messageThresholds[i] &&
            newProgress >= messageThresholds[i]
          ) {
            setMessageIndex(i);
            break;
          }
        }

        if (newProgress >= 99) {
          clearInterval(progressInterval);
          return 99;
        }

        return newProgress;
      });
    }, updateInterval);

    return () => {
      clearInterval(progressInterval);
    };
  }, [isGenerating]);

  // helper function to get current operation context
  const getOperationContext = (): OperationContext => {
    if (isModalAIServiceEnabled) {
      return {
        isSorting: sortDocuments,
        isSummary: generateSummary,
        documentCount: selectedDocumentIds.size,
      };
    } else {
      // legacy mode: checkbox controls sorting, unchecking generates summary
      return {
        isSorting: generateSortedDocument,
        isSummary: !generateSortedDocument, // only generates summary when NOT sorting
        documentCount: selectedDocumentIds.size,
      };
    }
  };

  const handleClose = () => {
    setSelectedDocumentIds(new Set());
    setIsGenerating(false);
    setProgress(0);
    setMessageIndex(0);
    onClose();
  };

  const handleGenerateNotes = async () => {
    if (selectedDocumentIds.size === 0) {
      KaboodleToaster.error("Please select at least one document");
      return;
    }

    // validation for new UI: require at least one option selected
    if (isModalAIServiceEnabled && !sortDocuments && !generateSummary) {
      KaboodleToaster.error(
        "Please select at least one option: Sort Documents or Generate Summary",
      );
      return;
    }

    try {
      setIsGenerating(true);

      const data: {
        document_ids: number[];
        generate_sorted_document?: boolean;
        job_options?: { sort: boolean; summarize: boolean };
      } = {
        document_ids: Array.from(selectedDocumentIds),
      };

      // add appropriate fields based on feature flag
      if (isModalAIServiceEnabled) {
        data.job_options = {
          sort: sortDocuments,
          summarize: generateSummary,
        };
      } else {
        data.generate_sorted_document = generateSortedDocument;
      }

      let result: { task_id: string };
      if (serviceType === "MCP" || serviceType === "LCP") {
        result = (await generateCostProjectionAllocatorNotes({
          costProjectionId: serviceId,
          data,
        }).unwrap()) as { task_id: string };
      } else if (serviceType === "DOCS") {
        result = (await generateAIDocumentAnalysis({
          documentManagementFileId: serviceId,
          data,
        }).unwrap()) as { task_id: string };
      } else {
        result = (await generateMSAAllocatorNotes({
          msaId: serviceId,
          data,
        }).unwrap()) as { task_id: string };
      }

      if (result.task_id) {
        const context = getOperationContext();
        const message = getSuccessMessage(context, serviceType);
        setTimeout(() => {
          KaboodleToaster.success(message);
          handleClose();
        }, 1000);
      }
    } catch (err) {
      const context = getOperationContext();
      const message = getErrorMessage(context, serviceType);
      console.error("Failed to generate allocator notes:", err);
      KaboodleToaster.error(message);
      setIsGenerating(false);
    }
  };

  const handleSelectedRowsChange = (selectedRows: Set<string | number>) => {
    setSelectedDocumentIds(new Set(Array.from(selectedRows) as number[]));
  };

  if (isGenerating) {
    const context = getOperationContext();
    const loadingMessages = getLoadingMessages(context);
    const loadingHeading = getLoadingHeading(context);

    return (
      <KaboodleModal
        isOpen={isOpen}
        onClose={handleClose}
        className="allocator-notes-modal"
      >
        <Card heading={loadingHeading}>
          <div className="flex jc-c">
            <div className="progress-indicator">
              <div className="progress-message">
                {loadingMessages[messageIndex]}
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${progress}%` }}
                >
                  <span className="progress-text">{Math.floor(progress)}%</span>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </KaboodleModal>
    );
  }

  return (
    <KaboodleModal
      isOpen={isOpen}
      onClose={handleClose}
      footer={
        <div className="flex jc-c">
          <Button onClick={handleGenerateNotes}>
            {serviceType === "DOCS" ? "Analyze Document" : "Generate Notes"}
          </Button>
          <Button onClick={handleClose}>Cancel</Button>
        </div>
      }
    >
      <Card
        heading={
          serviceType === "DOCS"
            ? "AI Document Analysis"
            : "Generate Allocator Notes"
        }
      >
        <div className="flex flex-col gap-4">
          {isLoading ? (
            <div>Loading documents...</div>
          ) : !data?.results || data.results.length === 0 ? (
            <div>No documents found</div>
          ) : (
            <Table
              columns={columns}
              rows={data.results}
              rowKeyGetter={(row) => row.id}
              selectedRows={selectedDocumentIds}
              onSelectedRowsChange={handleSelectedRowsChange}
              className="h-full"
            />
          )}
        </div>
        {/* only show checkbox for Document Management and Future Medical workspaces */}
        {(serviceType === "DOCS" ||
          serviceType === "MSA" ||
          serviceType === "MCP" ||
          serviceType === "LCP") && (
          <>
            {isModalAIServiceEnabled ? (
              <>
                <Checkbox
                  name="sort-documents"
                  label="Sort Documents"
                  checked={sortDocuments}
                  onChange={(e) => setSortDocuments(e.target.checked)}
                />
                <Checkbox
                  name="generate-summary"
                  label="Generate Summary"
                  checked={generateSummary}
                  onChange={(e) => setGenerateSummary(e.target.checked)}
                />
              </>
            ) : (
              <Checkbox
                name="generate-sorted-document"
                label="Sort Documents"
                checked={generateSortedDocument}
                onChange={(e) => setGenerateSortedDocument(e.target.checked)}
              />
            )}
          </>
        )}
      </Card>
    </KaboodleModal>
  );
};

export default GenerateAllocatorNotesModal;
